/**
 * Single source of truth for "is this device online?".
 *
 * Everything that renders or filters on connection state goes through the hooks here.
 * The underlying calculation is deliberately not exported: it needs the live mobile
 * config, and call sites that computed it without one used to apply a different
 * threshold from the rest of the dashboard — the same view could read online on the map
 * and offline in its own status panel.
 *
 * Two entry points, because not every caller can hold a hook:
 *  - [useConnectionStatus] for a single device rendered directly.
 *  - [useConnectionStatusFn] for loops, useMemo and event handlers, which get a bound
 *    function instead.
 *
 * Both re-evaluate on a timer, so a device that goes quiet turns offline on its own
 * without needing a Firestore update or a re-render to trigger it.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useMobileConfig } from '@/hooks/useMobileConfig';
import type { AndroidStatus } from '@/types';

export type ConnectionStatus =
  | 'unpaired'
  | 'never_connected'
  | 'offline'
  | 'survival'
  | 'online';

/**
 * Absolute grace granted on top of the expected heartbeat before a device is called
 * offline. Used when config/mobile carries no explicit value.
 *
 * A device is offline once nothing has been heard for `heartbeat + grace` — with the
 * standard 30s heartbeat, 60s. The grace is an absolute number of seconds rather than a
 * multiple of the heartbeat so it stays readable, and it still tracks the heartbeat:
 * raise the interval and the threshold follows without a second edit.
 *
 * 30s is one full write timeout (PresenceManager.WRITE_TIMEOUT_MS), so exactly one slow
 * write is absorbed without a healthy device flapping offline.
 */
export const DEFAULT_OFFLINE_GRACE_SECONDS = 30;

/** Fallback heartbeat, mirroring config/mobile. */
export const DEFAULT_HEARTBEAT_SECONDS = 30;

/**
 * Survival mode reports in roughly hourly (a 5 min alarm × 12 cycles), so it gets its
 * own threshold: past 2× that, the battery is presumed dead. Unrelated to the normal
 * heartbeat, hence not derived from it.
 */
const SURVIVAL_OFFLINE_THRESHOLD_MS = 2 * 60 * 60 * 1000;

/** How often the status is re-evaluated against the clock. */
const POLL_INTERVAL_MS = 10_000;

/**
 * Offline threshold in ms. Exported so the admin panel and status UI can display the
 * effective value instead of re-deriving the sum themselves.
 */
export function getOfflineThresholdMs(
  heartbeatIntervalSeconds: number | undefined,
  offlineGraceSeconds: number | undefined
): number {
  const hb = heartbeatIntervalSeconds ?? DEFAULT_HEARTBEAT_SECONDS;
  const grace = offlineGraceSeconds ?? DEFAULT_OFFLINE_GRACE_SECONDS;
  return (hb + grace) * 1000;
}

function computeStatus(
  token: string | undefined,
  androidStatus: AndroidStatus | null | undefined,
  thresholdMs: number
): ConnectionStatus {
  if (!token) return 'unpaired';
  if (!androidStatus?.lastSeen) return 'never_connected';
  // Set by the app on a deliberate shutdown, so we can report offline immediately
  // instead of waiting out the threshold.
  if (androidStatus.shutdown) return 'offline';

  const lastSeenMs = Date.now() - androidStatus.lastSeen.toDate().getTime();

  if (androidStatus.capturing?.reason === 'survival_mode') {
    return lastSeenMs <= SURVIVAL_OFFLINE_THRESHOLD_MS ? 'survival' : 'offline';
  }

  return lastSeenMs <= thresholdMs ? 'online' : 'offline';
}

/** Offline threshold currently in force, from the live mobile config. */
export function useOfflineThresholdMs(): number {
  const mobileConfig = useMobileConfig();
  return getOfflineThresholdMs(
    mobileConfig?.heartbeatIntervalSeconds,
    mobileConfig?.offlineGraceSeconds
  );
}

/**
 * Connection status of one device, kept current against the clock.
 */
export function useConnectionStatus(
  token: string | undefined,
  androidStatus: AndroidStatus | null | undefined
): ConnectionStatus {
  const thresholdMs = useOfflineThresholdMs();

  const [status, setStatus] = useState<ConnectionStatus>(() =>
    computeStatus(token, androidStatus, thresholdMs)
  );
  const prevStatusRef = useRef(status);

  useEffect(() => {
    const recalc = () => {
      const next = computeStatus(token, androidStatus, thresholdMs);
      if (next !== prevStatusRef.current) {
        prevStatusRef.current = next;
        setStatus(next);
      }
    };

    recalc();
    const id = setInterval(recalc, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [token, androidStatus, thresholdMs]);

  return status;
}

/**
 * Bound status function, for callers that cannot hold a hook per device: marker loops,
 * list filters inside useMemo, event handlers.
 *
 * The returned identity changes on every poll tick, so a useMemo that lists it as a
 * dependency recomputes as devices age out — a filter on "online" stays accurate
 * instead of freezing at whatever it computed on the last render.
 */
export function useConnectionStatusFn(): (
  token: string | undefined,
  androidStatus: AndroidStatus | null | undefined
) => ConnectionStatus {
  const thresholdMs = useOfflineThresholdMs();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return useMemo(
    () =>
      // `tick` is read here purely so it counts as a dependency: the returned identity
      // has to change on every poll for dependent memos to recompute as devices age
      // out. Without it a filter on "online" would keep whatever it computed on the
      // last render, however stale.
      (token: string | undefined, androidStatus: AndroidStatus | null | undefined) => {
        void tick;
        return computeStatus(token, androidStatus, thresholdMs);
      },
    [thresholdMs, tick]
  );
}
